import React from "react"
import Select, { Props } from "react-select"
import { ErrorMessage } from "../ErrorMessage"

type SelectOptionType = {
    value: string
    label: string
}

const variants = {
    fill: {
        red_500_19: "bg-red-500_19 text-red-500",
        teal_200_19: "bg-teal-200_19 text-teal-200",
    },
    outline: {
        gray_800_64: "border border-gray-800_64 border-solid text-teal-200",
    },
} as const

const shapes = {
    square: "rounded-none",
    round: "rounded-lg",
} as const

const sizes = {
    xs: "p-2",
    sm: "pb-3 pl-3 pr-[11px] pt-3.5",
} as const

export type SelectProps = Omit<Props<SelectOptionType, boolean>, "getOptionLabel"> &
    Partial<{
        children: React.ReactNode
        placeholder: string
        className: string
        options: SelectOptionType[]
        isSearchable: boolean
        placeholderClassName: string
        isMulti: boolean
        onChange: (option: any) => void
        value: any
        errors: string[]
        indicator: React.ReactElement
        getOptionLabel: (e: any) => string
        shape: keyof typeof shapes
        variant: keyof typeof variants
        size: keyof typeof sizes
        color: string
    }>

const SelectBox = React.forwardRef<any, SelectProps>(
    (
        {
            children,
            placeholder = "Select",
            className = "",
            options = [],
            isSearchable = false,
            placeholderClassName = "",
            isMulti = false,
            onChange,
            value = null,
            errors = [],
            indicator,
            shape,
            size,
            variant,
            color = "",
            ...restProps
        },
        ref,
    ) => {
        const normalizeValue = React.useCallback(
            (val: any) => {
                if (val === null || val === undefined) return null
                const opts = options as SelectOptionType[]
                if (isMulti) {
                    const arr = Array.isArray(val) ? val : [val]
                    return arr.map((v: any) =>
                        typeof v === "object" ? v : opts.find((o) => o.value === v) ?? { value: v, label: v }
                    )
                }
                if (typeof val === "object") return val
                return opts.find((o) => o.value === val) ?? { value: val, label: val }
            },
            [options, isMulti],
        )

        const [selectedVal, setSelectedVal] = React.useState<any>(() => normalizeValue(value))

        React.useEffect(() => {
            setSelectedVal(normalizeValue(value))
        }, [value, normalizeValue])

        const handleChange = (data: any) => {
            setSelectedVal(data)
            if (isMulti) {
                onChange?.(data?.map((d: SelectOptionType) => d.value) || [])
            } else {
                onChange?.(data?.value ?? "")
            }
        }

        const variantClass =
            variant && color
                ? variants[variant]?.[color as keyof (typeof variants)[typeof variant]] || ""
                : ""

        return (
            <>
                <Select
                    ref={ref}
                    options={options}
                    className={`react-select-container ${className} ${(shape && shapes[shape]) || ""} ${(size && sizes[size]) || ""} ${variantClass}`}
                    classNamePrefix="react-select"
                    placeholder={<div className={placeholderClassName}>{placeholder}</div>}
                    isSearchable={isSearchable}
                    isMulti={isMulti}
                    components={{
                        IndicatorSeparator: () => null,
                        ...(indicator ? { DropdownIndicator: () => indicator } : {}),
                    }}
                    value={selectedVal}
                    onChange={handleChange}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    menuPlacement="auto"
                    menuShouldBlockScroll={false}
                    styles={{
                        container: (provided) => ({
                            ...provided,
                            width: "100%",
                            position: "relative",
                        }),
                        control: (provided) => ({
                            ...provided,
                            backgroundColor: "transparent",
                            border: "0",
                            boxShadow: "none",
                            minHeight: "auto",
                            cursor: "pointer",
                            "&:hover": {
                                border: "0",
                            },
                        }),
                        valueContainer: (provided) => ({
                            ...provided,
                            padding: "0",
                        }),
                        input: (provided) => ({
                            ...provided,
                            color: "inherit",
                            margin: "0",
                            padding: "0",
                        }),
                        singleValue: (provided) => ({
                            ...provided,
                            color: "inherit",
                        }),
                        dropdownIndicator: (provided) => ({
                            ...provided,
                            paddingTop: 0,
                            paddingBottom: 0,
                            cursor: "pointer",
                        }),
                        clearIndicator: (provided) => ({
                            ...provided,
                            padding: 0,
                            cursor: "pointer",
                        }),
                        multiValueLabel: (provided) => ({
                            ...provided,
                            padding: 0,
                        }),
                        menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999999,
                            pointerEvents: "auto",
                        }),
                        menu: (base) => ({
                            ...base,
                            zIndex: 9999999,
                            pointerEvents: "auto",
                            backgroundColor: "#ffffff",
                            borderRadius: 12,
                            boxShadow: "0 10px 30px rgba(0, 0, 0, 0.12)",
                            overflow: "hidden",
                        }),
                        menuList: (base) => ({
                            ...base,
                            maxHeight: 240,
                            paddingTop: 4,
                            paddingBottom: 4,
                        }),
                        option: (base, state) => ({
                            ...base,
                            cursor: "pointer",
                            backgroundColor: state.isFocused
                                ? "#f5f5f5"
                                : state.isSelected
                                    ? "#eeeeee"
                                    : "#ffffff",
                            color: "#180e73",
                        }),
                    }}
                    {...restProps}
                />
                <ErrorMessage errors={errors} />
                {children}
            </>
        )
    },
)

SelectBox.displayName = "SelectBox"

export { SelectBox }
